import { ReactNode } from "react";

import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import { Alert, Button, Stack, Typography } from "@mui/material";
import { ContentCard } from "@shared";

import { useTranslation } from "@/translations";

interface Props {
  /** The subject-specific explanation of where the invitation goes. */
  description: string;
  /** Shown alone when the subject has no usable address. */
  noEmailHint: string;
  hasEmail: boolean;
  onSend: () => void;
  /** Subject-specific extra conditions (client: no selection / address blocked). */
  sendDisabled?: boolean;
  isSending: boolean;
  /** The address UI: the client's Autocomplete + duplicate hint, or the volunteer's read-only row. */
  children: ReactNode;
}

/**
 * The shell of the initial-activation ("Initiale Freischaltung") card. How the address is chosen
 * differs per subject and comes in as children; sending stays a direct action — confirmation
 * dialogs guard only the actions that take an existing access away and the password reset.
 */
export function PortalInvitationCard({
  description,
  noEmailHint,
  hasEmail,
  onSend,
  sendDisabled = false,
  isSending,
  children,
}: Props) {
  const { t } = useTranslation();

  return (
    <ContentCard title={t("portalAccess.invitation.title")} grow={false}>
      {!hasEmail ? (
        <Alert severity="info">{noEmailHint}</Alert>
      ) : (
        <Stack spacing={2} alignItems="flex-start">
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>

          {children}

          <Button
            variant="contained"
            startIcon={<SendOutlinedIcon />}
            disabled={isSending || sendDisabled}
            onClick={onSend}
          >
            {t("portalAccess.invitation.send")}
          </Button>
        </Stack>
      )}
    </ContentCard>
  );
}
